import { Component, OnDestroy, OnInit, WritableSignal, signal } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { Observable, Subject } from 'rxjs';
import {
	editingFile,
	replacingInProgress,
	selectActiveTab,
} from '@app/pages/cmms/components/files/state/files.selectors';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { FileLoaderService } from '@shared/ui-components/au-file-loader/services/file-loader.service';
import {
	startDeletingFile,
	startEditFile,
	updateFileInsideFileEntity,
} from '@app/pages/cmms/components/files/state/files.action';
import { FileUtilities } from '@shared/ui-components/au-file-loader/helpers/file-utilities';
import { FileEditHeaderComponent } from './file-edit-header/file-edit-header.component';
import { FileNotificationComponent } from '../file-notification/file-notification.component';
import { FileViewerComponent } from '@app/shared/ui-components/au-file-loader/file-viewer-modal/file-viewer/file-viewer.component';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EditFileEntityFormComponent } from './edit-file-entity-form/edit-file-entity-form.component';
import { FileDetectionButtonComponent } from '../file-detection-button/file-detection-button.component';

@Component({
	selector: 'edit-file-entity',
	templateUrl: './edit-file-entity.component.html',
	styleUrls: ['./edit-file-entity.component.scss'],
	imports: [
		FileEditHeaderComponent,
		FileNotificationComponent,
		FileViewerComponent,
		CommonModule,
		MatProgressSpinnerModule,
		EditFileEntityFormComponent,
		FileDetectionButtonComponent,
	],
})
export class EditFileEntityComponent implements OnInit, OnDestroy {
	isFullscreen$: Observable<boolean>;
	fileClassification: WritableSignal<string> = signal('');
	fileEntity: WritableSignal<IFileEntity> = signal(null);
	activeTab: 'view' | 'info' = 'view';
	replacingInProgress = this.store$.select(replacingInProgress);
	dataExists: WritableSignal<boolean> = signal(false);
	fileType: WritableSignal<string> = signal('');
	incidentsDetectedAndCreated: WritableSignal<object> = signal({});

	private destroy$: Subject<void> = new Subject<void>();

	constructor(
		private store$: Store<ICmmsState>,
		private fileLoaderService: FileLoaderService
	) {}

	ngOnInit(): void {
		this.getFileForEdit();
		this.store$
			.select(selectActiveTab)
			.pipe(takeUntil(this.destroy$))
			.subscribe(tabList => {
				const activeTab = tabList[this.fileEntity().pk] || 'view';
				this.activeTab = activeTab;
			});
	}

	private getFileForEdit() {
		this.store$
			.select(editingFile)
			.pipe(takeUntil(this.destroy$))
			.subscribe(file => {
				this.fileEntity.set(file);
				this.fileClassification.set(file.type);
				this.fileType.set(file.file.contentType);
			});
	}

	replaceFileButtonClick(fileInput: HTMLElement) {
		fileInput.click();
	}

	replaceFile(event: Event) {
		const file = FileUtilities.getFilesFromEvent(event)[0];
		this.store$.dispatch(
			updateFileInsideFileEntity({
				newFile: file,
				fileEntity: this.fileEntity(),
			})
		);
	}

	downloadFile() {
		this.fileLoaderService.saveFileOnUserComputer(
			this.fileEntity().file.url,
			this.fileEntity().file.name
		);
	}

	deleteFile() {
		this.store$.dispatch(startDeletingFile({ pk: this.fileEntity().pk }));
	}

	editEntityName($event: string) {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.fileEntity().pk,
					name: $event,
					notes: this.fileEntity().notes,
				},
			})
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
